import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  input,
  model,
  output,
  signal,
  viewChild
} from '@angular/core';
import {AppContext} from "../../../../app-context";
import {
  DeclarationStatus,
  IE3ConsignmentProcessSummary,
  IE3ConsignmentStatus,
  IE3CustomsProcess,
  IE3DischargeStatus,
  IE3EnsStatus,
  IE3InputDeclareTranshipment,
  IE3RefreshConsignmentProcessSummary,
  IE3TemporaryStorageFilingStatus,
  TaskMessageStatus
} from "@portbase/bezoekschip-service-typescriptmodels";
import lodash from "lodash";
import {
  CheckboxSelectionState,
  executeWhen,
  nonEmpty,
  nonNull,
  openEditModal,
  sendCommand
} from "../../../../common/utils";
import {
  MasterConsignmentDetailsComponent,
  MasterConsignmentDetailsComponentData
} from "../../details/master-consignment-details/master-consignment-details.component";
import {ConsignmentUtils, EquipmentSummaryItem, GoodsItemSummaryWithHouseConsignments} from "../../consignment.utils";
import {DeclarationMessageStatus} from "../../../visit-overview/visit-overview-item/visit-overview.utils";
import {SentenceCasePipe} from "../../../../common/sentence-case.pipe";
import {PortvisitUtils} from "../../../../refdata/portvisit-utils";
import {MiddleEllipsisPipe} from "../../../../common/middle-ellipsis.pipe";
import {ConsignmentRules} from "../../consignment.rules";
import {NewPortvisitUtils} from "../../../new-portvisit.utils";
import {ArrivalStatus, fromVisitStatus, MovementInfo} from "../../../common/movement-icons/movement-icons.component";

@Component({
  selector: 'app-master-consignment-overview-item',
  templateUrl: './master-consignment-overview-item.component.html',
  styleUrls: ['./master-consignment-overview-item.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MasterConsignmentOverviewItemComponent {
  portVisitUtils = PortvisitUtils;
  utils = ConsignmentUtils;
  appContext = AppContext;
  entries = Object.entries;

  readonly consignment = model.required<IE3ConsignmentProcessSummary>();
  readonly term = input<string>();
  readonly depth = input<number>();
  readonly selectable = input<boolean>();
  readonly selected = input<boolean>(false);
  readonly selectionToggled = output<void>();
  readonly checkboxSelectionState = CheckboxSelectionState;

  readonly collapseButton = viewChild<ElementRef>("collapseButton");

  selectionWidth: number = 40;
  collapsed = signal(true);
  childItemsShownByFilter = signal<string[]>([]);

  readonly temporaryStorageStatus = computed(() => this.getTemporaryStorageStatus());

  readonly customsNlStatus = computed(() => [this.temporaryStorageStatus(), this.getInspectionStatus(), this.getClearanceStatus(), this.getRenStatus()]);

  readonly customsEuStatus = computed(() => [this.getEnsStatus(), this.getNotificationOfArrivalStatus()].filter(nonNull)
    .filter(a => a.taskStatus !== TaskMessageStatus.DISABLED));

  readonly terminalStatuses = computed(() => [this.getDischargeStatus(), this.getBulkDischargeStatus(), this.getMidStatus()].filter(nonNull)
    .filter(a => a.taskStatus !== TaskMessageStatus.DISABLED));

  readonly collapsible = computed(() => {
    const c = this.consignment();
    return !!c.masterConsignment.equipments?.length || !!c.masterConsignment.goodsItems?.length
      || !!lodash.flatMap(c.masterConsignment.houseConsignments, h => h.goodsItems).length;
  });

  readonly consignmentEquipment = computed<EquipmentSummaryItem[]>(() =>
    ConsignmentUtils.goodsItemsWithContainerForSummary(this.consignment(), this.consignment().masterConsignment.equipments));

  readonly goodsItems = computed<GoodsItemSummaryWithHouseConsignments[]>(() =>
    ConsignmentUtils.bulkGoodsItemsForSummary(this.consignment()));

  readonly movementsOfPlaces = computed<MovementInfo[]>(() => {
    const consignment = this.consignment();
    return [<MovementInfo>{
      name: consignment.masterConsignment.placeOfLoading?.name
        ? `${consignment.masterConsignment.placeOfLoading.name} (${consignment.masterConsignment.placeOfLoading.countryUnCode})`
        : 'Unknown',
      date: consignment.masterConsignment.loadingAtd || consignment.masterConsignment.loadingEtd,
      arrivalStatus: consignment.masterConsignment.loadingAtd ? ArrivalStatus.departed : ArrivalStatus.expected,
      tooltip: 'Port of loading'
    }].concat(consignment.transhipment && consignment.masterConsignment.placeOfDelivery ? [<MovementInfo>{
      name: `${consignment.masterConsignment.placeOfDelivery.name} (${consignment.masterConsignment.placeOfDelivery.countryUnCode})`,
      arrivalStatus: ArrivalStatus.expected,
      date: null,
      tooltip: 'Place of delivery'
    }] : [<MovementInfo>{
      name: consignment.masterConsignment.placeOfUnloading?.name
        ? `${consignment.masterConsignment.placeOfUnloading.name} (${consignment.masterConsignment.placeOfUnloading.countryUnCode})`
        : 'Unknown',
      date: consignment.masterConsignment.unloadingAta || consignment.masterConsignment.unloadingEta,
      arrivalStatus: fromVisitStatus(consignment.arrivalStatus),
      tooltip: 'Port of discharge'
    }]);
  });

  readonly movementsOfTerminal = computed<MovementInfo>(() => {
    const consignment = this.consignment();
    return consignment.masterConsignment.dischargeTerminal ? {
      name: SentenceCasePipe.format(consignment.masterConsignment.dischargeTerminal.terminalName),
      date: consignment.masterConsignment.unloadingAta || consignment.masterConsignment.unloadingEta,
      arrivalStatus: fromVisitStatus(consignment.arrivalStatus),
      tooltip: 'Terminal'
    } : null;
  });

  readonly vehicleValue = computed(() => {
    const c = this.consignment();
    return SentenceCasePipe.format(c.masterConsignment.vehicle?.name)
      || c.masterConsignment.vehicle?.identificationNumber || 'Unknown';
  });

  readonly equipmentCount = computed(() =>
    lodash.sum([this.consignment().masterConsignment.equipments.length]));

  readonly totalWeight = computed(() => {
    const c = this.consignment();
    return c.straight
      ? lodash.round(lodash.sum(lodash.flatMap(c.masterConsignment.houseConsignments,
        h => h.goodsItems.map(g => g.grossWeight))), 1)
      : lodash.round(lodash.sum(c.masterConsignment.goodsItems.map(g => g.grossWeight)), 1);
  });

  readonly showTsStatus = computed(() =>
    !!this.temporaryStorageStatus() && (!!this.consignment().masterConsignment?.dischargeTerminal
      || !['UNKNOWN', 'DISABLED'].includes(this.temporaryStorageStatus().taskStatus)));

  editConsignment = () => openEditModal(MasterConsignmentDetailsComponent, <MasterConsignmentDetailsComponentData>{
    consignmentProcessId: this.consignment().consignmentProcessId,
  }, {
    warnOnClose: true,
    currentToStack: true
  });

  refreshSummary = () => {
    sendCommand('com.portbase.bezoekschip.common.api.consignments.commands.RefreshConsignmentProcessSummary', <IE3RefreshConsignmentProcessSummary>{
      consignmentProcessId: this.consignment().consignmentProcessId
    }, (summary: IE3ConsignmentProcessSummary) => {
      this.consignment.set(summary);
      AppContext.registerSuccess('Consignment summary has been rebuild successfully.');
    });
  }

  validSelector = (input: string): string => "SELECTOR_" + input.replace(/[^a-zA-Z0-9]/g, '');

  getEnsStatus = (): DeclarationMessageStatus => {
    const c = this.consignment();
    const status = c.status;
    return {
      name: "Entry Summary Declaration",
      taskStatus: ConsignmentRules.isTemporaryStorageOnlyConsignment(c.filingType) ? TaskMessageStatus.DISABLED : c.ensTaskStatus,
      messages: this.getMessagesEns(c.ensTaskStatus, status),
      customLabel: c.ensStatus != null ?
        PortvisitUtils.enumFormatter(c.ensStatus).replace("Ens", "ENS") : null,
      sentenceCase: false,
      customCssClass: [IE3EnsStatus.ENS_FILING_REGISTERED, IE3EnsStatus.AMENDMENT_COMPLETE_NOTIFICATION].includes(c.ensStatus)
        ? "text-portbase-green" : null,
      asInnerHtml: [IE3EnsStatus.CONTROL_NOTIFICATION, IE3EnsStatus.ADVANCE_CONTROL_NOTIFICATION].includes(c.ensStatus)
    }
  }

  getTemporaryStorageStatus = (): DeclarationMessageStatus => {
    const c = this.consignment();
    return {
      name: "Temporary storage",
      taskStatus: c.temporaryStorageStatus === IE3TemporaryStorageFilingStatus.WAITING_FOR_ENS
        ? TaskMessageStatus.WARNING : c.temporaryStorageTaskStatus,
      messages: ConsignmentUtils.getTemporaryStorageStatusMessage(
        c.status?.filingStatusTemporaryStorage, c.temporaryStorageStatus)
        .map(e => e.summary),
      customLabel: c.temporaryStorageStatus != null
        ? ConsignmentUtils.temporaryStorageStatusValueFormatter(c.temporaryStorageStatus) : null
    }
  }

  private getInspectionStatus(): DeclarationMessageStatus {
    const c = this.consignment();
    return ConsignmentUtils.getInspectionsStatus([lodash.last(c.masterConsignment.bulkInspections)]
      .concat(c.masterConsignment?.equipments?.map(e => lodash.last(e.inspections)))
      .filter(nonNull));
  }

  private getClearanceStatus(): DeclarationMessageStatus {
    const c = this.consignment();
    const clearance = c.status?.clearanceStatus;
    const taskStatus = clearance?.writtenOff ? TaskMessageStatus.ACCEPTED : clearance?.expirySummary ? TaskMessageStatus.WARNING : TaskMessageStatus.DISABLED;
    const goodsItems = ConsignmentUtils.getGoodsOfConsignment(c)
      .map(g => g.goodsItem);
    return {
      name: "Temporary storage clearance",
      taskStatus: taskStatus,
      messages: taskStatus === TaskMessageStatus.WARNING ? clearance.expirySummary.items.map(i => {
        const remainings = [i.remainingWeight ? `<div class="row" style="width: 80%"><span class="col">Remaining weight: </span><span class="ms-auto col-auto">${i.remainingWeight}</span></div>` : ""]
          .concat([i.remainingPackages ? `<div class="row"><span class="col">Remaining packages: </span><span class="ms-auto col-auto">${i.remainingPackages}</span></div>` : ""]).filter(nonEmpty);
        const goodsItem = goodsItems.find(g => g.goodsItemNumber === i.itemNumber);
        return `<div class="d-block">${MiddleEllipsisPipe.format(ConsignmentUtils.goodsItemsAsText(goodsItem), 20)}: <div class="d-block">${remainings.join("")}</div></div>`;
      }) : [],
      customLabel: clearance?.writtenOff ? "Written off" : clearance?.expirySummary ? "Differences in:" : "",
      asInnerHtml: true,
      hideComment: true
    };
  }

  private getDischargeStatus(): DeclarationMessageStatus {
    const c = this.consignment();
    const numberOfEquipmentMatchingStatus = (status: IE3DischargeStatus) => c.masterConsignment.equipments
      .filter(e => e.dischargeStatus === status).length;
    const amountShortLanded = numberOfEquipmentMatchingStatus(IE3DischargeStatus.SHORTLANDED);
    const amountDischarged = numberOfEquipmentMatchingStatus(IE3DischargeStatus.DISCHARGED);
    return {
      name: "Discharges",
      taskStatus: amountShortLanded ? TaskMessageStatus.ERROR : amountDischarged ? TaskMessageStatus.ACCEPTED : TaskMessageStatus.DISABLED,
      messages: [],
      customLabel: amountShortLanded ? "Shortlanded equipment" : amountDischarged ? "Discharged" : ""
    }
  }

  private getBulkDischargeStatus(): DeclarationMessageStatus {
    const c = this.consignment();
    const goodsItems = (c.masterConsignment.goodsItems || [])
      .concat(lodash.flatMap(c.masterConsignment.houseConsignments || [], (h => h.goodsItems)));
    const amountWithBulkDischarge = goodsItems.filter(g => g.bulkDischarge != null).length;
    const taskStatus = goodsItems.filter(g => g.bulkDischarge && g.bulkDischarge.grossWeight !== g.grossWeight)
      .map(g => `Terminal reported discharge of ${g.bulkDischarge.grossWeight}kg gross weight`);
    return {
      name: "Discharges",
      taskStatus: taskStatus.length ? TaskMessageStatus.WARNING : amountWithBulkDischarge ? TaskMessageStatus.ACCEPTED : TaskMessageStatus.DISABLED,
      messages: [],
      customLabel: taskStatus?.length ? "Bulk discharge differences" : amountWithBulkDischarge ? "Discharged" : ""
    }
  }

  getRenStatus = (): DeclarationMessageStatus => {
    const c = this.consignment();
    if (c.transhipment == null) {
      return null;
    }
    if (c.masterConsignment?.customsProcess !== IE3CustomsProcess.SEA_IN_SEA_OUT) {
      return null;
    }
    if (c.transhipment.scheduled) {
      return {
        name: "Re-export notification",
        taskStatus: TaskMessageStatus.SCHEDULED,
        messages: []
      }
    }
    const renStatus = c.transhipment.renStatus;
    const taskStatus = renStatus == null ? TaskMessageStatus.UNKNOWN
      : NewPortvisitUtils.declarationStatusToTaskMessageStatus(renStatus.status);
    return {
      name: "Re-export notification",
      taskStatus: taskStatus,
      messages: taskStatus === TaskMessageStatus.REJECTED ? [renStatus.reason] : [],
      customLabel: PortvisitUtils.enumFormatter(taskStatus)
    }
  }

  getMidStatus = (): DeclarationMessageStatus => {
    const c = this.consignment();
    if (c.transhipment == null) {
      return null;
    }
    if (c.masterConsignment?.customsProcess !== IE3CustomsProcess.SEA_IN_SEA_OUT) {
      return null;
    }
    const midStatus = c.transhipment.midStatus;
    // Temporary code until the replayer is done: derive the effective status without
    // mutating the (immutable) model held in the signal.
    const effectiveStatus = midStatus?.sent && !(midStatus?.status == DeclarationStatus.REJECTED) &&
      ["APMRTM", "APMII", "BCW", "RWG", "UNIP"].some(t => t === c.masterConsignment?.dischargeTerminal?.organisationShortName)
      ? DeclarationStatus.ACCEPTED : midStatus?.status;
    const taskStatus = midStatus == null ? TaskMessageStatus.UNKNOWN
      : NewPortvisitUtils.declarationStatusToTaskMessageStatus(effectiveStatus);
    return {
      name: "Notification import documentation",
      taskStatus: taskStatus,
      messages: taskStatus === TaskMessageStatus.REJECTED ? [midStatus.reason] : [],
      customLabel: PortvisitUtils.enumFormatter(taskStatus)
    }
  }

  getNotificationOfArrivalStatus = (): DeclarationMessageStatus => {
    const c = this.consignment();
    if (c.fromVisit) {
      return null;
    }
    const taskStatus = c.status?.notificationOfArrivalStatus || TaskMessageStatus.UNKNOWN;
    return {
      name: "Notification of Arrival",
      taskStatus: c.ensTaskStatus === TaskMessageStatus.DISABLED || ConsignmentRules.isTemporaryStorageOnlyConsignment(c.filingType) ? TaskMessageStatus.DISABLED : taskStatus,
      messages: [],
      customLabel: taskStatus === TaskMessageStatus.ACCEPTED ? "Accepted" : PortvisitUtils.enumFormatter(taskStatus)
    }
  }

  private getMessagesEns = (taskStatus: TaskMessageStatus, status: IE3ConsignmentStatus): string[] =>
    ConsignmentUtils.getMessagesEns(null, taskStatus, status).map(e => e.summary);

  duplicate = () => {
    openEditModal(MasterConsignmentDetailsComponent, <MasterConsignmentDetailsComponentData>{
      copyOfConsignmentProcessId: this.consignment().consignmentProcessId
    });
  }

  createTranshipment = () =>
    sendCommand('com.portbase.bezoekschip.common.api.consignments.commands.input.InputDeclareTranshipment',
      <IE3InputDeclareTranshipment>{
        cargoDeclarantId: this.consignment().iamConnectedId,
        consignmentNumber: this.consignment().masterConsignment.consignmentNumber
      }, () => {
        AppContext.registerSuccess("Declaration transhipment sent successfully");
      });

  expandOverviewItem(id: string) {
    if (id) {
      this.childItemsShownByFilter.update(items => [...items, id]);
    }
    executeWhen(() => !!this.collapseButton(),
      () => {
        const target = this.collapseButton().nativeElement.getAttribute('data-bs-target');
        const el = document.getElementById(target.replace('#', ''));
        if (el) {
          el.classList.add('show');
          this.collapseButton().nativeElement?.setAttribute('aria-expanded', 'true');
          this.collapsed.set(false);
        }
      }, 10);
  }

  trackByKey = (index: number, entry: [string, string]): string => entry[0];
}
